import { useApolloClient } from "@apollo/client/react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { RiskMetrics, Deal } from "@/lib/types/database/deals";
import {
  GetCeoRiskDealsDocument,
  GetCeoRiskDealsQuery,
  GetCeoRiskRatedCompaniesDocument,
  GetCeoRiskRatedCompaniesQuery,
  GetCeoRiskUnassignedTicketsDocument,
  GetCeoRiskUnassignedTicketsQuery,
  GetCeoRiskUsersDocument,
  GetCeoRiskUsersQuery,
} from "@/src/gql/graphql";
import {
  calculateAtRiskDeals,
  calculateAtRiskCompanies,
  calculateStaffingConstraints,
  type RiskCompanyInput,
  type RiskTicketInput,
  type RiskUserInput,
} from "./risk-metrics-calculators";
import { fetchAllGraphqlNodes } from "./paginate";

interface UseCeoRiskMetricsOptions {
  enabled?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

// Node types derived from the codegen query shapes (single source of truth).
type RiskDealNode = NonNullable<
  GetCeoRiskDealsQuery["dealsCollection"]
>["edges"][number]["node"];
type RiskCompanyNode = NonNullable<
  GetCeoRiskRatedCompaniesQuery["companiesCollection"]
>["edges"][number]["node"];
type RiskTicketNode = NonNullable<
  GetCeoRiskUnassignedTicketsQuery["ticketsCollection"]
>["edges"][number]["node"];
type RiskUserNode = NonNullable<
  GetCeoRiskUsersQuery["app_usersCollection"]
>["edges"][number]["node"];

const EMPTY_METRICS: RiskMetrics = {
  atRiskDeals: [],
  atRiskCompanies: [],
  staffingConstraints: {
    overallocatedStaff: [],
    capacityWarnings: [],
    unassignedWork: [],
  },
};

export function useCeoRiskMetricsGraphQL(options: UseCeoRiskMetricsOptions = {}) {
  const { isAuthenticated } = useUniversalAuth();
  const { enabled = true, dateFrom, dateTo } = options;
  const client = useApolloClient();

  if (dateFrom && dateTo) {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (from > to) {
      throw new Error("Start date must be before end date");
    }
  }

  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery<RiskMetrics>({
    queryKey: ["ceo-risk-metrics", dateFrom ?? null, dateTo ?? null],
    enabled: isAuthenticated && enabled,
    queryFn: async () => {
      // Each collection is paged to completion (never a silently-capped page).
      const [dealNodes, companyNodes, ticketNodes, userNodes] = await Promise.all([
        fetchAllGraphqlNodes<GetCeoRiskDealsQuery, RiskDealNode>(
          client,
          GetCeoRiskDealsDocument,
          { dateFrom, dateTo },
          (d) => d.dealsCollection,
        ),
        fetchAllGraphqlNodes<GetCeoRiskRatedCompaniesQuery, RiskCompanyNode>(
          client,
          GetCeoRiskRatedCompaniesDocument,
          {},
          (d) => d.companiesCollection,
        ),
        fetchAllGraphqlNodes<GetCeoRiskUnassignedTicketsQuery, RiskTicketNode>(
          client,
          GetCeoRiskUnassignedTicketsDocument,
          {},
          (d) => d.ticketsCollection,
        ),
        fetchAllGraphqlNodes<GetCeoRiskUsersQuery, RiskUserNode>(
          client,
          GetCeoRiskUsersDocument,
          {},
          (d) => d.app_usersCollection,
        ),
      ]);

      // The risk-deal node is a structural subset of `Deal` (calculators consume
      // `Deal[]` and `Deal` carries an index signature). Same narrowing the
      // single-query hook performed; not a cast-to-any.
      const deals = dealNodes as unknown as Deal[];
      const companies: RiskCompanyInput[] = companyNodes;
      const tickets: RiskTicketInput[] = ticketNodes;
      const users: RiskUserInput[] = userNodes;

      const atRiskDeals = calculateAtRiskDeals(deals);
      const atRiskCompanies = calculateAtRiskCompanies(atRiskDeals, companies);
      const staffingConstraints = calculateStaffingConstraints(users, deals, tickets);

      return { atRiskDeals, atRiskCompanies, staffingConstraints };
    },
  });

  return {
    metrics: metrics ?? EMPTY_METRICS,
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  };
}
